/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { getFgActiveFiltersForGa } from '../../utils/filters';

type Search = Record<string, any>;

/** Number of results the filters in the dialog would show */
export type ResultsCount =
    { status: 'loading' } | { status: 'error' } | { status: 'success'; total: number };

/**
 * Results count of the applied filters, from the `searchResults` store state.
 * @param searchResults `searchResults` store state, which is untyped
 */
export function getAppliedResultsCount(searchResults: object): ResultsCount {
    if ('loading' in searchResults && searchResults.loading === true) {
        return { status: 'loading' };
    }
    const total =
        'total' in searchResults && typeof searchResults.total === 'number'
            ? searchResults.total
            : 0;
    return { status: 'success', total };
}

/** Label of the dialog's results button for the results count */
export function getResultsButtonLabel(count: ResultsCount) {
    if (count.status !== 'success') {
        return 'Show Results';
    }
    if (count.total === 0) {
        return 'No Results';
    }
    const resultsLabel = count.total === 1 ? 'Result' : 'Results';
    return `Show ${count.total.toLocaleString('en-US')} ${resultsLabel}`;
}

/**
 * Number of selected filters. Same count as the filters badge in
 * `SearchCriteriaInputs` and the desktop `SearchFilters` panel, which uses it to
 * show "Clear All" and to label the results button.
 */
export function getSearchFiltersCount(search: Search) {
    let filterCount = 0;

    if (search && search.uiFilters) {
        const { uiFilters } = search;

        filterCount += Object.keys(uiFilters.occupants).length;
        filterCount += Object.keys(uiFilters.activities).length;
        if (uiFilters.activityTags) {
            filterCount += Object.keys(uiFilters.activityTags).length;
        }
        filterCount += Object.keys(uiFilters.agencies).length;
        filterCount += get(search, 'fg', []).length;
        filterCount += Object.keys(uiFilters.campsiteVehicleLength).length;
        filterCount += Object.keys(uiFilters.tourTypes).length;
        filterCount += Object.keys(uiFilters.tourAccessible).length;
        filterCount += Object.keys(uiFilters.tourDifficulty).length;
        filterCount += Object.keys(uiFilters.tourDuration).length;
        filterCount += Object.keys(uiFilters.tourPrice).length;
        filterCount += Object.keys(uiFilters.tourTime).length;
        if (uiFilters.tourTimes) {
            filterCount += Object.keys(uiFilters.tourTimes).length;
        }
        filterCount += Object.keys(uiFilters.permitTypes).length;
        filterCount += Object.keys(uiFilters.inventoryTypes).length;
    }

    return filterCount;
}

/**
 * Selected filters sent to GA with the results button click. Same string as
 * the desktop `SearchFilters` panel builds for its "View Results" button.
 */
export function getActiveFiltersForGa(search: Search) {
    const showFilter = (
        visibleFilterHeader: string,
        filterCategoryName: string,
        hasPipe = true
    ) => {
        let filterSegment = '';

        if (Object.keys(search.uiFilters[filterCategoryName]).length > 0) {
            filterSegment = `${
                hasPipe ? '|' : ''
            } ${visibleFilterHeader}: ${Object.keys(search.uiFilters[filterCategoryName])} `;
        }

        return filterSegment;
    };

    const showAvailabilityFilters = (visibleFilterHeader: string, filterType: string) => {
        let filterSegment = '';

        if (get(search, filterType, false) === false) {
            filterSegment = `| ${visibleFilterHeader} `;
        }

        return filterSegment;
    };

    const showSizeFilters = (visibleFilterHeader: string, filterType: string) => {
        let filterSegment = '';

        const getSize =
            search?.uiFilters[filterType][filterType]?.filters[0]?.filterValue || [];

        if (getSize.length > 0) {
            filterSegment = `| ${visibleFilterHeader}: ${filterType}: ${getSize} `;
        }

        return filterSegment;
    };

    let activeFilters = '';

    if (search && search.uiFilters) {
        // Camping
        activeFilters += showFilter('Booking Types', 'inventoryTypes', false);

        if (process.env?.SEARCH?.showFederalAgencies) {
            activeFilters += showFilter(
                'Federal Agencies Offering Reservations',
                'agencies',
                false
            );
        }

        activeFilters += showFilter('Allowable Equipment', 'campsiteEquipment');
        activeFilters += showFilter('Recreation Site Type', 'campsiteTypes');
        activeFilters += showFilter('Amenities', 'campsiteAmenities');
        activeFilters += showFilter('Electrical Hookup', 'campsiteElectricityHookup');

        // Tours
        activeFilters += showFilter('Tour Types', 'tourTypes');
        activeFilters += showFilter('Tour Accessible', 'tourAccessible');
        activeFilters += showFilter('Level of Effort', 'tourDifficulty');

        // Permits
        if (process.env?.ALLOWED_INVENTORY_TYPES?.permits) {
            activeFilters += showFilter('Permits', 'permitTypes');
        }

        // Activities
        activeFilters += showFilter('Activities', 'activities');

        activeFilters += showAvailabilityFilters(
            'Hide Unavailable',
            'include_unavailable'
        );
        activeFilters += showAvailabilityFilters(
            'Hide First-come, First-served',
            'include_notreservable'
        );
        activeFilters += showAvailabilityFilters(
            'Hide Partially Available',
            'include_partially_available'
        );

        // Group & Vehicle Size
        activeFilters += showSizeFilters('Group Size', 'occupants');
        activeFilters += showSizeFilters('Vehicle Length (ft)', 'campsiteVehicleLength');
    }

    const fg = get(search, 'fg', []);

    if (!isEmpty(fg)) {
        activeFilters += getFgActiveFiltersForGa(fg);
    }

    return activeFilters;
}

// cSpell:ignore notreservable
